import React, { useCallback, useState } from 'react';
import { Create, useNotify, useRedirect } from 'react-admin';
import { FormSpy } from 'react-final-form';
import { withStyles } from '@material-ui/core/styles';

import FundTagSummary from './FundTagSummary';
import FormTitleBar from '../../core/components/FormTitleBar';
import CreateToolbar from '../../core/components/CreateToolbar';
import NavigationPrompt from '../../core/components/NavigationPrompt';
import LeaveConfirm from '../../core/components/LeaveConfirmWrapper';
import CustomSimpleForm from '../../core/components/form/CustomSimpleForm';
import useNotifyServerError from '../../core/hooks/useNotifyServerError';
import FundTagApi from '../../core/apis/FundTagApi';
import FundTagsAssociationsApi from '../../core/apis/FundTagsAssociationsApi';
import { getIcError } from '../../core/providers/utils';

const styles = () => ({
  noActions: {
    marginTop: '0px'
  }
});

const FundTagTitle = () => {
  return (
    <FormTitleBar
      type="Fund Tag"
      creation
      showAvatar
      avatarContent={'FT'}
      recordLabel="Fund Tag"
    />
  );
};

const FundTagForm = props => {
  const { setWhenBlockNavigation } = props;

  const redirect = useRedirect();
  const notify = useNotify();
  const notifyServerError = useNotifyServerError();

  const handleFormIsDirty = useCallback(
    dirty => {
      // Block navigation when the form is dirty
      setWhenBlockNavigation(dirty);
    },
    [setWhenBlockNavigation]
  );

  const allowNavigationOnSave = () => {
    setWhenBlockNavigation(false);
  };

  const onSave = async values => {
    let hasPostError = false;
    const payload = { name: values.name, description: values.description };
    const data = await FundTagApi.create(payload).catch(err => {
      hasPostError = true;
      notifyServerError(err);
    });

    if (!hasPostError) {
      if (data.id && values.fundIds.length > 0) {
        const fundIdsToAdd = values.fundIds.map(id => {
          return { fundTagId: `${data.id}`, fundId: `${id}` };
        });
        FundTagsAssociationsApi.create(fundIdsToAdd)
          .then(() => {
            setWhenBlockNavigation(false);
            notify('ra.notification.created');
            redirect(`/fundtags/${data.id}`);
          })
          .catch(err => {
            notify('ra.notification.server', {
              type: 'warning',
              messageArgs: {
                message:
                  'Fund Tag has been created without associations. ' +
                  getIcError(err),
                isForkedWarning: true
              }
            });
          });
      } else {
        setWhenBlockNavigation(false);
        notify('ra.notification.created');
        redirect(`/fundtags/${data.id}`);
      }
    }
  };

  return (
    <CustomSimpleForm
      save={onSave}
      margin="normal"
      variant="standard"
      toolbar={<CreateToolbar allowNavigationOnSave={allowNavigationOnSave} />}
    >
      <FundTagSummary creation />
      <FormSpy
        subscription={{ dirty: true, values: true }}
        onChange={e => handleFormIsDirty(e.dirty)}
      />
    </CustomSimpleForm>
  );
};

const FundTagCreate = props => {
  const { classes } = props;
  const [whenBlockNavigation, setWhenBlockNavigation] = useState(false);

  return (
    <React.Fragment>
      <NavigationPrompt when={whenBlockNavigation} creation>
        {(isOpen, onConfirm, onCancel) => (
          <LeaveConfirm
            isOpen={isOpen}
            onConfirm={onConfirm}
            onClose={onCancel}
          />
        )}
      </NavigationPrompt>
      <Create
        {...props}
        title={<FundTagTitle />}
        classes={{ noActions: classes.noActions }}
        record={{
          name: '',
          fundIds: [],
          description: ''
        }}
      >
        <FundTagForm setWhenBlockNavigation={setWhenBlockNavigation} />
      </Create>
    </React.Fragment>
  );
};

export default withStyles(styles)(FundTagCreate);
